import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { Bell, ChevronRight } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useApi } from '../hooks/useApi';
import { GlobalSearch } from './GlobalSearch';

interface Crumb {
  label: string;
  to?: string;
}

function useBreadcrumbs(): Crumb[] {
  const location = useLocation();
  const { patientId } = useParams<{ patientId: string }>();
  const path = location.pathname;

  const crumbs: Crumb[] = [{ label: 'Dashboard', to: '/dashboard' }];

  if (path === '/') {
    return crumbs;
  }

  if (path.startsWith('/patients')) {
    crumbs.push({ label: 'Patients', to: '/patients' });

    if (path === '/patients/new') {
      crumbs.push({ label: 'Register' });
    } else if (patientId) {
      crumbs.push({ label: 'Patient' });
    }
  }

  if (path === '/pending-registrations') {
    crumbs.push({ label: 'Pending Registrations' });
  }

  if (path === '/appointments') {
    crumbs.push({ label: 'Appointments' });
  }

  if (path.startsWith('/billing')) {
    crumbs.push({ label: 'Billing', to: '/billing' });
    if (path === '/billing/new') {
      crumbs.push({ label: 'New Invoice' });
    } else if (path !== '/billing') {
      crumbs.push({ label: 'Invoice' });
    }
  }

  if (path === '/settings/consumables') {
    crumbs.push({ label: 'Consumables' });
  }

  if (path === '/settings/icd10') {
    crumbs.push({ label: 'ICD-10 Management' });
  }

  if (path === '/settings/nhrpl') {
    crumbs.push({ label: 'NHRPL Management' });
  }

  if (path.startsWith('/clinical/')) {
    crumbs.push({ label: 'Patients', to: '/patients' });
    crumbs.push({ label: 'Clinical' });
  }

  if (path === '/waiting-list') {
    crumbs.push({ label: 'Waiting List' });
  }

  if (path === '/settings/formulary') {
    crumbs.push({ label: 'Drug Formulary' });
  }

  if (path === '/whatsapp') {
    crumbs.push({ label: 'WhatsApp' });
  }

  if (path === '/claims') {
    crumbs.push({ label: 'Claims' });
  }

  if (path === '/reports') {
    crumbs.push({ label: 'Reports' });
  }

  if (path === '/settings/users') {
    crumbs.push({ label: 'User Management' });
  }

  if (path === '/settings/hospitals') {
    crumbs.push({ label: 'Hospitals & Venues' });
  }

  if (path === '/logbook') {
    crumbs.push({ label: 'Surgical Logbook' });
  }

  return crumbs;
}

interface AppLayoutProps {
  children: React.ReactNode;
}

export function AppLayout({ children }: AppLayoutProps) {
  const { currentUser, userRole, logout } = useAuth();
  const { get } = useApi();
  const crumbs = useBreadcrumbs();
  const [pendingCount, setPendingCount] = useState(0);

  const fetchPendingCount = useCallback(async () => {
    try {
      const result = await get<{ count: number }>('/pending-patients/count');
      if (result.success && result.data) {
        setPendingCount(result.data.count);
      }
    } catch {
      // non-critical
    }
  }, [get]);

  useEffect(() => {
    fetchPendingCount();
    const interval = setInterval(fetchPendingCount, 60_000);
    // Listen for immediate refresh events from PendingRegistrationsPage
    const handler = () => fetchPendingCount();
    window.addEventListener('pending-count-changed', handler);
    return () => {
      clearInterval(interval);
      window.removeEventListener('pending-count-changed', handler);
    };
  }, [fetchPendingCount]);

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between h-14">
            {/* Left: logo + breadcrumbs */}
            <div className="flex items-center gap-3">
              <Link to="/dashboard" className="text-lg font-bold text-gray-900 hover:text-blue-600 transition-colors flex-shrink-0">
                IKhan Gynae
              </Link>
              <div className="hidden sm:flex items-center gap-1 text-sm text-gray-500">
                {crumbs.map((crumb, i) => (
                  <React.Fragment key={i}>
                    {i > 0 && <ChevronRight className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />}
                    {crumb.to && i < crumbs.length - 1 ? (
                      <Link to={crumb.to} className="hover:text-gray-700 transition-colors">
                        {crumb.label}
                      </Link>
                    ) : (
                      <span className="text-gray-900 font-medium">{crumb.label}</span>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>

            {/* Right: search + bell + user info + sign out */}
            <div className="flex items-center gap-4">
              <GlobalSearch />
              <Link to="/pending-registrations" className="relative p-1.5 text-gray-500 hover:text-gray-700 transition-colors" title="Pending registrations">
                <Bell className="h-5 w-5" />
                {pendingCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 h-4 min-w-[1rem] px-1 flex items-center justify-center text-[10px] font-bold text-white bg-red-500 rounded-full">
                    {pendingCount > 99 ? '99+' : pendingCount}
                  </span>
                )}
              </Link>
              <span className="text-sm text-gray-600 hidden sm:inline">
                {currentUser?.email}
                <span className="ml-2 inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                  {userRole}
                </span>
              </span>
              <button
                onClick={logout}
                className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </nav>
      {children}
    </div>
  );
}
